import React, { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import useDialog from '../../hooks/useDialog';
import { formatRs } from '../../utils/money';

// The pieces of a bill the counter lets staff change: the discount unit, a line's discount, a short
// line's quantity, and the two ways to add a line. They used to call the API themselves against a
// saved invoice. They now hand the value to a callback instead, because at the counter the bill can
// still be an unsaved preview, and the first change is what saves it (see BillingCounter). The
// rules they apply are unchanged, and the server enforces every one of them regardless.

/** Keeps digits and at most one decimal point with two places. For money typed as text. */
export const cleanDecimal = (raw) => {
  const s = String(raw ?? '').replace(/[^0-9.]/g, '');
  const dot = s.indexOf('.');
  if (dot === -1) return s;
  return `${s.slice(0, dot + 1)}${s.slice(dot + 1).replace(/\./g, '').slice(0, 2)}`;
};

/**
 * Whether this bill states its line discounts as percentages or as rupee amounts. Set once for the
 * whole bill; switching converts what is already entered (the server does it) rather than clearing.
 *
 * @param {(mode: string) => Promise<unknown>} onChange
 */
export const InvoiceDiscountModeToggle = ({ mode, onChange, disabled = false }) => {
  const [saving, setSaving] = useState(false);
  const set = async (next) => {
    if (next === mode || saving || disabled) return;
    setSaving(true);
    try {
      await onChange(next);
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="inline-flex items-center gap-1.5 mr-1">
      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Discount</span>
      <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden" role="group" aria-label="Discount unit">
        {[{ key: 'PERCENT', text: '%' }, { key: 'AMOUNT', text: 'Rs' }].map((o) => (
          <button
            key={o.key}
            type="button"
            onClick={() => set(o.key)}
            disabled={saving || disabled}
            aria-pressed={mode === o.key}
            title={`Every line's discount on this bill is ${o.key === 'PERCENT' ? 'a percentage' : 'a rupee amount'}`}
            className={`px-2.5 py-1 text-xs font-semibold transition-colors disabled:opacity-50 ${
              mode === o.key ? 'bg-emerald-600 text-white' : 'bg-white text-gray-500 hover:bg-gray-50'
            }`}
          >
            {o.text}
          </button>
        ))}
      </div>
    </div>
  );
};

/**
 * The quantity on a line the pharmacy cannot fully cover. The only quantity at the counter that can
 * be edited, and only downwards: every other quantity comes from the prescription.
 *
 * @param {(quantity: number) => Promise<boolean>} onSave resolves true when the change was saved
 */
export const ShortfallQuantityInput = ({ quantity, available, onSave, showToast, label }) => {
  const [value, setValue] = useState(String(quantity));
  const [saving, setSaving] = useState(false);
  const lastSaved = useRef(quantity);
  useEffect(() => { setValue(String(quantity)); lastSaved.current = quantity; }, [quantity]);

  const save = async (raw) => {
    const n = parseInt(raw, 10);
    if (!n || n === lastSaved.current) { setValue(String(lastSaved.current)); return; }
    if (n > lastSaved.current) {
      showToast('error', 'A billed quantity can only be reduced.');
      setValue(String(lastSaved.current));
      return;
    }
    setSaving(true);
    try {
      const ok = await onSave(n);
      if (ok) lastSaved.current = n;
      else setValue(String(lastSaved.current));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col items-center gap-1">
      <input
        type="number"
        min="1"
        max={quantity}
        value={value}
        aria-label={label ? `Quantity of ${label}` : 'Quantity'}
        onChange={(e) => setValue(e.target.value)}
        onBlur={(e) => save(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); } }}
        disabled={saving}
        className="w-16 px-2 py-1.5 border border-red-300 rounded-lg text-sm text-center focus:ring-1 focus:ring-red-500 focus:border-red-500 disabled:opacity-50"
      />
      <div className="text-[10px] font-semibold text-red-600 leading-tight">only {available} in stock</div>
      {/* One click to the number that actually clears the block. */}
      {available > 0 && (
        <button
          type="button"
          onClick={() => save(String(available))}
          disabled={saving}
          className="text-[10px] font-semibold text-red-700 underline hover:no-underline disabled:opacity-50"
        >
          Bill {available}
        </button>
      )}
    </div>
  );
};

/**
 * One line's discount, in whichever unit the bill states them. Saves on blur or Enter. Typed as text
 * with a decimal keypad, formatted by nothing while typing, so the caret never jumps.
 *
 * @param {(value: number) => Promise<boolean>} onSave resolves true when the change was saved
 */
export const ItemDiscountInput = ({ currentDiscount, percentMode, onSave, label }) => {
  const shown = (n) => (Number(n) > 0 ? String(Number(n)) : '');
  const [value, setValue] = useState(shown(currentDiscount));
  const [saving, setSaving] = useState(false);
  const lastSaved = useRef(Number(currentDiscount) || 0);
  // Re-seed when the mode flips or the server returns a different figure: the line is now quoting a
  // different number.
  useEffect(() => {
    setValue(shown(currentDiscount));
    lastSaved.current = Number(currentDiscount) || 0;
  }, [currentDiscount, percentMode]);

  const save = async () => {
    const num = parseFloat(value) || 0;
    if (num === lastSaved.current) { setValue(shown(lastSaved.current)); return; }
    setSaving(true);
    try {
      const ok = await onSave(num);
      if (ok) lastSaved.current = num;
      else setValue(shown(lastSaved.current));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="relative w-24 ml-auto">
      {!percentMode && <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">Rs</span>}
      <input
        type="text"
        inputMode="decimal"
        autoComplete="off"
        value={value}
        aria-label={label ? `Discount on ${label}` : 'Discount'}
        onChange={(e) => setValue(cleanDecimal(e.target.value))}
        onBlur={save}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); } }}
        disabled={saving}
        placeholder="0"
        className={`w-full py-1.5 border border-gray-300 rounded-lg text-sm text-right tabular-nums focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 disabled:opacity-50 ${
          percentMode ? 'pl-2 pr-6' : 'pl-7 pr-2'
        }`}
      />
      {percentMode && <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">%</span>}
    </div>
  );
};

/** Pick a catalogue service to add to the bill. */
export const AddServiceModal = ({ services, onAdd, onClose }) => {
  const addServiceDialogRef = useDialog(onClose, { confirmDiscard: false });

  return (
    <div ref={addServiceDialogRef} className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[70vh] overflow-y-auto">
        <div className="px-6 py-4 flex items-center justify-between sticky top-0 bg-gradient-to-r from-navy-800 to-navy-700 rounded-t-2xl">
          <h3 className="text-lg font-bold text-white">Add Extra Service</h3>
          <button type="button" aria-label="Close" onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg"><X className="h-5 w-5 text-white" /></button>
        </div>
        <div className="p-4 space-y-2">
          {services.length === 0 ? (
            <p className="text-center text-gray-400 py-6">No active services available</p>
          ) : (
            services.map((s) => {
              let doctorName = null;
              if (s.doctor) {
                const base = (s.doctor.fullName || `${s.doctor.firstName || ''} ${s.doctor.lastName || ''}`).trim();
                doctorName = /^dr\.?\s/i.test(base) ? base : `Dr. ${base}`;
              }
              return (
                <button type="button" key={s.id} onClick={() => onAdd(s.id)} className="w-full p-3 text-left bg-gray-50 hover:bg-emerald-50 rounded-xl flex items-center justify-between transition-colors">
                  <div className="min-w-0">
                    <div className="font-medium text-gray-900 flex items-center gap-2 flex-wrap">
                      <span>{s.name}</span>
                      {doctorName && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                          {doctorName}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500">{s.serviceType?.replace('_', ' ')}</div>
                  </div>
                  <span className="font-semibold text-gray-700 whitespace-nowrap ml-3 tabular-nums">{formatRs(s.baseFee)}</span>
                </button>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

/** An ad-hoc line that is not in the service catalogue. A name, a price above zero and a quantity. */
export const CustomItemModal = ({ onAdd, onClose }) => {
  const [form, setForm] = useState({ name: '', description: '', unitPrice: '', quantity: '1' });
  const price = parseFloat(form.unitPrice);
  const qty = parseInt(form.quantity, 10);
  const valid = form.name.trim() && price > 0 && qty >= 1;

  const handleSubmit = () => {
    if (!valid) return;
    onAdd({ name: form.name.trim(), description: form.description.trim() || null, unitPrice: price, quantity: qty });
  };

  const customItemDialogRef = useDialog(onClose);

  return (
    <div ref={customItemDialogRef} className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
        <div className="px-6 py-4 flex items-center justify-between bg-gradient-to-r from-navy-800 to-navy-700">
          <h3 className="text-lg font-bold text-white">Add Custom Item</h3>
          <button type="button" aria-label="Close" onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg"><X className="h-5 w-5 text-white" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label htmlFor="custom-item-name" className="block text-sm font-medium text-gray-700 mb-1">Item Name <span className="text-red-500">*</span></label>
            <input id="custom-item-name" type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500" placeholder="e.g., Lab Test Fee" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="custom-item-price" className="block text-sm font-medium text-gray-700 mb-1">Unit Price (Rs) <span className="text-red-500">*</span></label>
              <input id="custom-item-price" type="text" inputMode="decimal" autoComplete="off" value={form.unitPrice} onChange={(e) => setForm({ ...form, unitPrice: cleanDecimal(e.target.value) })} className="w-full p-3 border border-gray-300 rounded-xl tabular-nums" placeholder="500" />
            </div>
            <div>
              <label htmlFor="custom-item-qty" className="block text-sm font-medium text-gray-700 mb-1">Quantity <span className="text-red-500">*</span></label>
              <input id="custom-item-qty" type="text" inputMode="numeric" autoComplete="off" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value.replace(/[^0-9]/g, '') })} className="w-full p-3 border border-gray-300 rounded-xl tabular-nums" />
            </div>
          </div>
          <div>
            <label htmlFor="custom-item-description" className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <input id="custom-item-description" type="text" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full p-3 border border-gray-300 rounded-xl" placeholder="Additional details" />
          </div>
        </div>
        <div className="flex justify-end gap-3 px-6 pb-6">
          <button type="button" onClick={onClose} className="px-5 py-2.5 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium">Cancel</button>
          <button type="button" onClick={handleSubmit} disabled={!valid} className="px-5 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-medium disabled:opacity-50">Add Item</button>
        </div>
      </div>
    </div>
  );
};
